/**
 * B.3 · Ecosystem Management — API client + presentational helpers.
 *
 * The B.2 backend is the SINGLE authority for whether an operator may
 * perform any lifecycle action.  Every entity response now carries a
 * server-derived `available_actions` (list of action_ids the caller
 * may execute right now) and `action_reasons` (minimal reason codes
 * for contextually-disabled actions the UI should still surface —
 * currently only `approve` with `creator_cannot_approve_own` or
 * `enhanced_reviewer_required`).
 *
 * This module therefore contains NO permission logic, NO lifecycle
 * transition matrix, and NO creator/enhanced-approval computation.
 * It only:
 *   • wraps the B.2 REST contract,
 *   • maps server errors to approved copy,
 *   • gates workspace navigation by role (a pure UX shell — the
 *     backend still enforces every endpoint independently).
 */
import { api, formatApiErrorDetail } from "./auth";

// ---- Locked B.2 constants (mirror of backend) ---------------------
export const LIFECYCLE_STATES = [
  "DRAFT", "REVIEW", "REJECTED", "APPROVED",
  "PATIENT_VISIBLE", "INACTIVE", "EXPIRED", "ARCHIVED", "RETIRED",
];

export const STATE_LABELS = {
  DRAFT: "Draft",
  REVIEW: "In review",
  REJECTED: "Rejected",
  APPROVED: "Approved",
  PATIENT_VISIBLE: "Patient-visible",
  INACTIVE: "Inactive",
  EXPIRED: "Expired",
  ARCHIVED: "Archived",
  RETIRED: "Retired",
};

export const STATE_TOOLTIPS = {
  DRAFT: "Not yet submitted for review.",
  REVIEW: "Awaiting reviewer decision.",
  REJECTED: "A reviewer rejected this version. Read the reason and revise.",
  APPROVED: "Approved by a reviewer, not yet published to patients.",
  PATIENT_VISIBLE: "Live for patients right now.",
  INACTIVE: "Paused. Not visible to patients.",
  EXPIRED: "Time-expired. Not visible to patients.",
  ARCHIVED: "Cold storage. Can be unarchived for a fresh review cycle.",
  RETIRED: "Terminal. Historical only. No changes possible.",
};

export const ENHANCED_DOMAINS = new Set(["legal", "immigration", "therapists"]);
export const ENHANCED_CATEGORIES = new Set(["clinical_trials", "insurance_benefits"]);

export const DOMAIN_LABELS = {
  legal: "Legal",
  housing: "Housing",
  immigration: "Immigration",
  retail: "Retail",
  nourishment: "Nourishment",
  wellness: "Wellness",
  trainers: "Trainers",
  coaches: "Coaches",
  therapists: "Therapists",
  aesthetics: "Aesthetics",
};

export const CATEGORY_LABELS = {
  clinical_trials: "Clinical Trials / Cancer Research",
  insurance_benefits: "Insurance & Benefits",
};

export const ENTITY_TYPES = [
  "organization", "provider", "program", "event", "resource",
  "prayer_circle", "trainer", "coach", "therapist", "restaurant",
  "insurance_resource", "clinical_trial_resource", "housing_resource",
];

// ---- Role sets used only for workspace navigation (categorical
// role visibility, NOT per-entity action authorization).  The server
// still enforces every endpoint independently.
export const OPERATOR_ROLES = new Set([
  "admin", "super_admin", "content_operator", "ecosystem_operator",
  "reviewer", "enhanced_reviewer", "read_only_operator",
  "event_operator", "relationship_manager",
]);
const GLOBAL_AUDIT_ROLES = new Set(["admin", "super_admin", "read_only_operator"]);
const CREATOR_ROLES = new Set(["content_operator", "ecosystem_operator", "admin", "super_admin"]);
const REVIEW_QUEUE_ROLES = new Set([
  "admin", "super_admin", "reviewer", "enhanced_reviewer", "read_only_operator",
]);

// ---- Enhanced-review label (pure presentation) -------------------
export function enhancedTriggerLabel(sc) {
  if (!sc?.enhanced_review) return null;
  const dom = (sc.enhanced_domain_triggers || []).map((d) => DOMAIN_LABELS[d] || d);
  const cat = (sc.enhanced_category_triggers || []).map((c) => CATEGORY_LABELS[c] || c);
  const all = [...dom, ...cat];
  return all.length ? `Enhanced — ${all.join(" + ")}` : "Enhanced";
}

// ---- Reason-code → operator-facing tooltip copy ------------------
// Server emits stable reason codes.  Only the UI turns them into
// human-readable strings, so backend copy changes never require a
// deploy of the client and vice versa.
export const ACTION_REASON_COPY = {
  creator_cannot_approve_own: "Creator cannot approve their own version. Ask another reviewer.",
  enhanced_reviewer_required: "Enhanced approval required. Ask an enhanced reviewer.",
};

export function reasonCopy(code) {
  return ACTION_REASON_COPY[code] || null;
}

// ---- Capability-driven workspace visibility ----------------------
// These are shell/navigation gates, not authorization.  A patient
// role hitting /ecosystem must not see workspace chrome at all.  The
// backend still rejects every request such a user could send.
//
// BTC3 Foundation completion · Gate 3 — helpers now take the
// capability projection from `useCapabilities()` and no longer read
// `user.role`.  Backwards-compat with `user`-shaped callers is
// preserved so any legacy import continues to work while migration
// completes.
export function canSeeWorkspace(caps) {
  if (!caps) return false;
  return !!(
    caps.ecosystem_entity_create ||
    caps.ecosystem_entity_approve_standard ||
    caps.ecosystem_entity_approve_enhanced ||
    caps.ecosystem_relationship_approve ||
    caps.ecosystem_relationship_revoke ||
    caps.ecosystem_grounding_authorize ||
    caps.ecosystem_read_only
  );
}
export function canSeeGlobalAudit(caps) {
  if (!caps) return false;
  return !!(caps.ecosystem_read_only || caps.ecosystem_relationship_revoke);
}
export function canSeeReviewQueue(caps) {
  if (!caps) return false;
  return !!(caps.ecosystem_entity_approve_standard || caps.ecosystem_entity_approve_enhanced);
}
export function canCreateEntity(caps) {
  if (!caps) return false;
  return !!caps.ecosystem_entity_create;
}

// ---- Error mapping (approved §H copy) ----------------------------
export function mapServerError(err) {
  const status = err?.response?.status;
  const detail = formatApiErrorDetail(err?.response?.data?.detail);
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) {
    if (typeof detail === "string" && detail.includes("creator_cannot_approve_own_version")) {
      return "Creator cannot approve their own version. Ask another reviewer.";
    }
    return "Your role does not allow this action.";
  }
  if (status === 409) {
    if (typeof detail === "string" && /already/i.test(detail)) {
      return detail; // already-transitioned message from server is human-readable.
    }
    return "This entity's state changed. Refresh to see the current state before retrying.";
  }
  if (status === 422) return detail || "Please correct the highlighted fields.";
  if (status >= 500) return "Something went wrong on our side. This action was not applied.";
  return detail || "Something went wrong. Please try again.";
}

// ---- Endpoint calls (thin wrappers over the B.2 contract) --------
const base = "/admin/ecosystem/entities";

export const EcosystemAPI = {
  list: () => api.get(base).then((r) => r.data.items || []),
  create: (payload) => api.post(base, payload).then((r) => r.data),
  get: (id) => api.get(`${base}/${id}`).then((r) => r.data),
  versions: (id) => api.get(`${base}/${id}/versions`).then((r) => r.data.items || []),
  audit: (id) => api.get(`${base}/${id}/audit`).then((r) => r.data.items || []),
  edit: (id, body) => api.post(`${base}/${id}/edit`, body).then((r) => r.data),
  submit: (id) => api.post(`${base}/${id}/submit`, {}).then((r) => r.data),
  approve: (id) => api.post(`${base}/${id}/approve`, {}).then((r) => r.data),
  reject: (id, reason) => api.post(`${base}/${id}/reject`, { reason }).then((r) => r.data),
  resubmit: (id) => api.post(`${base}/${id}/resubmit`, {}).then((r) => r.data),
  publish: (id) => api.post(`${base}/${id}/publish`, {}).then((r) => r.data),
  deactivate: (id, reason) => api.post(`${base}/${id}/deactivate`, { reason }).then((r) => r.data),
  emergencyRevoke: (id, reason) => api.post(`${base}/${id}/emergency-revoke`, { reason }).then((r) => r.data),
  expire: (id) => api.post(`${base}/${id}/expire`, {}).then((r) => r.data),
  archive: (id) => api.post(`${base}/${id}/archive`, {}).then((r) => r.data),
  unarchive: (id) => api.post(`${base}/${id}/unarchive`, {}).then((r) => r.data),
  retire: (id) => api.post(`${base}/${id}/retire`, {}).then((r) => r.data),
  // Phase B.5 — grounding authorization workflow.  Server-side
  // authoritative: the ecosystem.entity.authorize_grounding permission
  // gates authorize/reject/revoke; request is content-operator eligible.
  groundingRequest: (id, version, reason) => api.post(`${base}/${id}/grounding/request`, { version, reason: reason || null }).then((r) => r.data),
  groundingAuthorize: (id, version, reason) => api.post(`${base}/${id}/grounding/authorize`, { version, reason: reason || null }).then((r) => r.data),
  groundingReject: (id, version, reason) => api.post(`${base}/${id}/grounding/reject`, { version, reason }).then((r) => r.data),
  groundingRevoke: (id, version, reason) => api.post(`${base}/${id}/grounding/revoke`, { version, reason }).then((r) => r.data),
  groundingHistory: (id) => api.get(`${base}/${id}/grounding/history`).then((r) => r.data),
  // Phase B.6 — relationship workflow.
  listRelationships: (params = {}) => api.get(`/admin/ecosystem/relationships`, { params }).then((r) => r.data),
  createRelationship: (body) => api.post(`/admin/ecosystem/relationships`, body).then((r) => r.data),
  submitRelationship: (id) => api.post(`/admin/ecosystem/relationships/${id}/submit`, {}).then((r) => r.data),
  approveRelationship: (id) => api.post(`/admin/ecosystem/relationships/${id}/approve`, {}).then((r) => r.data),
  rejectRelationship: (id, reason) => api.post(`/admin/ecosystem/relationships/${id}/reject`, { reason }).then((r) => r.data),
  deactivateRelationship: (id, reason) => api.post(`/admin/ecosystem/relationships/${id}/deactivate`, { reason }).then((r) => r.data),
  emergencyRevokeRelationship: (id, reason) => api.post(`/admin/ecosystem/relationships/${id}/emergency-revoke`, { reason }).then((r) => r.data),
};
